import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'

import { Skeleton } from 'antd'
import classnames from 'classnames'

import PagedList from 'core/application/commons/pagedList'
import { PriorRecurringExpenseResponse, getPriorPremiums } from 'core/application/expenses/getPriorPremiums'

import CustomCollapse from 'common/components/dataDisplay/CustomCollapse'

import useQueryRequest from 'common/hooks/useQueryRequest'

import { parseZoneFormat } from 'common/dateFormatters'

import Icon from '../../../../common/components/general/Icon'
import { ROUTE_PATHS } from '../../../../common/routePaths'
import DocumentComponent from '../../../common/DocumentComponet'
import ExpenseReviewIcon from '../../../common/ExpenseReviewStatusIcon'

import './styles.less'

type PriorPremiumsProps = {
  premiumId: string
  onPriorPremiumClick?: (recurringExpenseId: string) => void
}

const PriorPremiums: React.FC<PriorPremiumsProps> = (props) => {
  const { t: translate } = useTranslation()
  const parentScrollDiv = useRef(null)

  const [currentQueueNumber, setCurrentQueueNumber] = useState<number>(1)
  const [hasMoreQueueItems, setHasMoreQueueItems] = useState<boolean>(true)
  const [queueLoading, setQueueLoading] = useState<boolean>(false)

  const [, isPriorPremiumsPagedLoading, sendGetPriorPremiums] = useQueryRequest(null)
  const [priorPremiumsPaged, setPriorPremiumsPaged] = useState<PagedList<PriorRecurringExpenseResponse> | null>(null)

  useEffect(() => {
    ;(async () => {
      setQueueLoading(true)
      const result = await sendGetPriorPremiums(() => getPriorPremiums(props.premiumId, currentQueueNumber))
      setPriorPremiumsPaged(result)
      setHasMoreQueueItems(result.pageCount > currentQueueNumber)
      setQueueLoading(false)
    })()
  }, [props.premiumId])

  const onPriorExpensesEndReach = async () => {
    if (!priorPremiumsPaged) return

    if (priorPremiumsPaged.pageCount < currentQueueNumber) return

    const nextPageNumber = currentQueueNumber + 1
    const result = await sendGetPriorPremiums(() => getPriorPremiums(props.premiumId, nextPageNumber))

    setPriorPremiumsPaged({
      ...result,
      items: [...priorPremiumsPaged.items, ...result.items],
    })
    setCurrentQueueNumber(nextPageNumber)
    setHasMoreQueueItems(result.pageCount > nextPageNumber)
  }

  return (
    <Skeleton loading={queueLoading} active paragraph={{ rows: 4, className: 'p-24' }} title={{ className: 'ml-24' }}>
      <div className="prior_expenses_header">
        <h6>{translate('doc-review-dashboard.prior-premiums.title')}</h6>
      </div>

      <div className="prior_expenses mr-24" ref={parentScrollDiv}>
        <InfiniteScroll
          pageStart={0}
          loadMore={() => onPriorExpensesEndReach()}
          hasMore={hasMoreQueueItems}
          useWindow={false}
          loader={
            isPriorPremiumsPagedLoading ? (
              <div className="loader" key={0}>
                {translate('general.loading')}
              </div>
            ) : (
              <></>
            )
          }
          getScrollParent={() => parentScrollDiv.current}
        >
          <>
            {priorPremiumsPaged &&
              priorPremiumsPaged.items.map((priorPremium: PriorRecurringExpenseResponse) => (
                <div
                  className={`${classnames({
                    prior_expense_item__active: priorPremium.isDuplicate,
                  })} prior_expense__item cursor_pointer`}
                  onClick={() => {
                    if (props.onPriorPremiumClick) {
                      props.onPriorPremiumClick(priorPremium.recurringExpenseId)
                    } else {
                      window
                        .open(`${ROUTE_PATHS.DOC_REVIEW_PREMIUMS}/${priorPremium.recurringExpenseId}`, '_blank')
                        .focus()
                    }
                  }}
                >
                  <div className="flex__space_between">
                    <p>
                      {priorPremium.isDuplicate &&
                        priorPremium.matchCount > 0 &&
                        Array.from({ length: priorPremium.matchCount }).map((_, index) => (
                          <Icon
                            key={index}
                            type="far fa-flag"
                            fontAwesome
                            className="color-red-6"
                            style={{ marginRight: '2px' }}
                          />
                        ))}
                      ${priorPremium.approvedAmount}
                    </p>
                  </div>

                  <div className="flex__space_between">
                    <p>{priorPremium.policyName}</p>
                  </div>

                  <div>
                    <h6>{priorPremium.expenseType.name}</h6>
                  </div>

                  <div className="flex__space_between flex__space_between_centered">
                    <span className="color-gray-7">Start date: {parseZoneFormat(priorPremium.approvedStartDate)}</span>
                    <span className="color-gray-7">End date: {parseZoneFormat(priorPremium.approvedEndDate)}</span>
                    {!priorPremium.isDuplicate && (
                      <div>
                        <ExpenseReviewIcon status={priorPremium.status} />
                      </div>
                    )}
                  </div>

                  {priorPremium.isDuplicate && (
                    <div className="flex__space_between">
                      <CustomCollapse
                        title=""
                        seeMoreTitle={translate('doc-review-dashboard.prior-expenses.see-more-documents')}
                        seeLessTitle={translate('doc-review-dashboard.prior-expenses.see-less-documents')}
                        itemsToShow={0}
                        items={[]}
                        contentOnly
                        content={<DocumentComponent documents={priorPremium.attachments} />}
                      />

                      <div>
                        <ExpenseReviewIcon status={priorPremium.status} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </>
        </InfiniteScroll>
      </div>
    </Skeleton>
  )
}

export default PriorPremiums
